import React, { useState, useRef, useEffect, Suspense, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { projectSlug, projectPath, SITE_URL } from "../projectSlugs";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { PerspectiveCamera } from "@react-three/drei";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import * as THREE from "three";
import PROJECTS from "../models";

const DEFAULT_LIGHTS = {
    ambientIntensity: 0.55,
    keyIntensity: 1.1,
    keyX: -6, keyY: 8, keyZ: 6,
    fillIntensity: 0.35,
    fillX: 6, fillY: 2, fillZ: 4,
    rimIntensity: 0.2,
    rimX: 0, rimY: -4, rimZ: -8,
    metalness: 0.7,
    roughness: 0.55,
};

const UNIQUE_PROJECTS = PROJECTS.filter((p, i, arr) => arr.findIndex(q => q.id === p.id) === i);

// Look up a model from a /work/<slug> path
function projectFromPath(pathname) {
    const m = pathname.match(/^\/work\/([^/]+)\/?$/);
    if (!m) return null;
    return UNIQUE_PROJECTS.find(p => projectSlug(p) === m[1]) ?? null;
}

// Tab bar state that shows a model's siblings
function navStateFor(project, hierarchy) {
    const cat = hierarchy.find(c => c.name === (project.category ?? "Other"));
    if (!cat) return { level: "category" };
    return { level: "model", category: cat.name, group: cat.isFlat ? null : (project.group || null) };
}

// Model files are stored as relative paths; make them absolute so they load from /work/... URLs too
function assetUrl(url) {
    if (!url || /^(https?:)?\/\//.test(url) || url.startsWith("/")) return url;
    return "/" + url;
}

function buildHierarchy(projects) {
    const catMap = new Map();
    projects.forEach(p => {
        const cat = p.category ?? "Other";
        // If p.group is missing or empty, we treat it as null (ungrouped)
        const grp = p.group || null;
        if (!catMap.has(cat)) catMap.set(cat, new Map());
        const grpMap = catMap.get(cat);
        if (!grpMap.has(grp)) grpMap.set(grp, []);
        grpMap.get(grp).push(p);
    });

    return Array.from(catMap.entries()).map(([catName, grpMap]) => {
        const groups = Array.from(grpMap.entries()).map(([grpName, models]) => ({
            name: grpName,
            models,
            hero: models[0],
        }));

        // A category is "flat" if it has only one group and that group has no name
        const isFlat = groups.length === 1 && groups[0].name === null;

        return {
            name: catName,
            groups,
            isFlat
        };
    });
}

function modelDebugDefaults(project) {
    return {
        rotX: project.rotation?.[0] ?? -Math.PI / 2,
        rotY: project.rotation?.[1] ?? 0,
        rotZ: project.rotation?.[2] ?? 0,
        rotationAxis: project.rotationAxis ?? "z",
        viewX: project.initialView?.[0] ?? 0,
        viewY: project.initialView?.[1] ?? 0,
        viewZ: project.initialView?.[2] ?? 10,
        initialZoom: project.initialZoom ?? 100,
    };
}

// ── Debug panel ───────────────────────────────────────────────────────────────
function Slider({ label, value, min, max, step = 0.05, onChange }) {
    return (
        <div style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px", gap: "6px" }}>
                <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", fontFamily: "monospace", flexShrink: 0 }}>{label}</span>
                <input
                    type="number" min={min} max={max} step={step}
                    value={Number(value).toFixed(step < 1 ? 2 : 0)}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v))); }}
                    style={{ width: "62px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "3px", color: "rgba(255,255,255,0.85)", fontSize: "10px", fontFamily: "monospace", padding: "1px 4px", textAlign: "right", flexShrink: 0 }}
                />
            </div>
            <input type="range" min={min} max={max} step={step} value={value}
                onChange={e => onChange(parseFloat(e.target.value))}
                style={{ width: "100%", accentColor: "#aaa", cursor: "pointer" }} />
        </div>
    );
}

function AxisSelect({ value, onChange }) {
    return (
        <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
            {["x", "y", "z"].map(ax => (
                <button key={ax} onClick={() => onChange(ax)} style={{
                    flex: 1, padding: "4px",
                    background: value === ax ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.05)",
                    border: value === ax ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.1)",
                    color: value === ax ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)",
                    borderRadius: "3px", cursor: "pointer", fontSize: "11px", fontFamily: "monospace",
                }}>{ax}</button>
            ))}
        </div>
    );
}

function LightDebugPanel({ lights, onChange, paused, onTogglePause, onReset, modelDebug, onModelDebugChange, activeProject }) {
    const [open, setOpen] = useState(true);
    const [tab, setTab] = useState("model");
    const set = (key) => (val) => onChange({ ...lights, [key]: val });
    const setMd = (key) => (val) => onModelDebugChange({ ...modelDebug, [key]: val });

    const piStr = (v) => {
        const frac = v / Math.PI;
        if (Math.abs(frac) < 0.001) return "0";
        if (Math.abs(Math.abs(frac) - 0.5) < 0.001) return frac > 0 ? "Math.PI / 2" : "-Math.PI / 2";
        if (Math.abs(Math.abs(frac) - 1) < 0.001) return frac > 0 ? "Math.PI" : "-Math.PI";
        if (Math.abs(Math.abs(frac) - 0.25) < 0.001) return frac > 0 ? "Math.PI / 4" : "-Math.PI / 4";
        return v.toFixed(3);
    };

    const printModelBlock = () => {
        const md = modelDebug; const l = lights;
        const block = `    {
        id: "${activeProject.id}",
        name: "${activeProject.name}",
        category: "${activeProject.category}",
        group: "${activeProject.group ?? "General"}",
        year: "${activeProject.year}",
        software: "${activeProject.software}",
        description: "${activeProject.description}",
        formats: ${JSON.stringify(activeProject.formats)},
        url: "${activeProject.url}",
        format: "${activeProject.format}",
        rotation: [${piStr(md.rotX)}, ${piStr(md.rotY)}, ${piStr(md.rotZ)}],
        rotationAxis: "${md.rotationAxis}",
        initialView: [${md.viewX.toFixed(1)}, ${md.viewY.toFixed(1)}, ${md.viewZ.toFixed(1)}],
        initialZoom: ${md.initialZoom},
        // ambient: ${l.ambientIntensity.toFixed(2)}, key: ${l.keyIntensity.toFixed(2)} @ [${l.keyX.toFixed(1)},${l.keyY.toFixed(1)},${l.keyZ.toFixed(1)}]
        // fill: ${l.fillIntensity.toFixed(2)} @ [${l.fillX.toFixed(1)},${l.fillY.toFixed(1)},${l.fillZ.toFixed(1)}]
        // rim: ${l.rimIntensity.toFixed(2)} @ [${l.rimX.toFixed(1)},${l.rimY.toFixed(1)},${l.rimZ.toFixed(1)}]
        // metalness: ${l.metalness.toFixed(2)}, roughness: ${l.roughness.toFixed(2)}
    },`;
        console.log(block);
        alert("Model block printed to console (F12) — paste into models.js");
    };

    const tabStyle = (t) => ({
        flex: 1, padding: "5px", cursor: "pointer", fontSize: "9px",
        letterSpacing: "0.1em", textTransform: "uppercase", border: "none",
        background: tab === t ? "rgba(255,255,255,0.1)" : "transparent",
        color: tab === t ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)",
        borderBottom: tab === t ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.08)",
    });

    return (
        <div style={{
            position: "fixed", bottom: "50px", right: 0, width: "260px",
            background: "rgba(0,0,0,0.92)", border: "1px solid rgba(255,255,255,0.12)",
            borderRight: "none", borderRadius: "8px 0 0 8px", zIndex: 9999,
            fontFamily: "monospace", overflow: "hidden",
        }}>
            <div onClick={() => setOpen(o => !o)} style={{
                padding: "8px 12px", borderBottom: open ? "1px solid rgba(255,255,255,0.08)" : "none",
                cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}>
                <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Debug</span>
                <button onClick={e => { e.stopPropagation(); onTogglePause(); }} style={{
                    background: paused ? "rgba(255,200,80,0.15)" : "rgba(255,255,255,0.08)",
                    border: paused ? "1px solid rgba(255,200,80,0.4)" : "1px solid rgba(255,255,255,0.15)",
                    color: paused ? "rgba(255,200,80,0.9)" : "rgba(255,255,255,0.6)",
                    fontSize: "10px", padding: "2px 8px", borderRadius: "3px", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap",
                }}>{paused ? "▶ play" : "⏸ pause"}</button>
                <button onClick={e => { e.stopPropagation(); onReset(); }} style={{
                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.45)", fontSize: "10px", padding: "2px 8px",
                    borderRadius: "3px", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap",
                }}>↺ reset</button>
                <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", marginLeft: "auto" }}>{open ? "▼" : "▲"}</span>
            </div>
            {open && (
                <>
                    <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                        <button style={tabStyle("model")} onClick={() => setTab("model")}>Model</button>
                        <button style={tabStyle("lights")} onClick={() => setTab("lights")}>Lights</button>
                        <button style={tabStyle("material")} onClick={() => setTab("material")}>Material</button>
                    </div>
                    <div style={{ padding: "10px 12px", maxHeight: "60vh", overflowY: "auto" }}>
                        {tab === "model" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Initial rotation</div>
                            <Slider label="x" value={modelDebug.rotX} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotX")} />
                            <Slider label="y" value={modelDebug.rotY} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotY")} />
                            <Slider label="z" value={modelDebug.rotZ} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Auto-rotate axis</div>
                            <AxisSelect value={modelDebug.rotationAxis} onChange={(v) => onModelDebugChange({ ...modelDebug, rotationAxis: v })} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Camera position</div>
                            <Slider label="x" value={modelDebug.viewX} min={-20} max={20} step={0.5} onChange={setMd("viewX")} />
                            <Slider label="y" value={modelDebug.viewY} min={-20} max={20} step={0.5} onChange={setMd("viewY")} />
                            <Slider label="z" value={modelDebug.viewZ} min={1} max={30} step={0.5} onChange={setMd("viewZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Initial zoom %</div>
                            <Slider label="zoom" value={modelDebug.initialZoom} min={20} max={300} step={1} onChange={setMd("initialZoom")} />
                            <button onClick={printModelBlock} style={{
                                width: "100%", marginTop: "12px",
                                background: "rgba(100,200,150,0.1)", border: "1px solid rgba(100,200,150,0.3)",
                                color: "rgba(100,200,150,0.9)", padding: "6px", cursor: "pointer",
                                borderRadius: "4px", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase",
                            }}>Copy block to console</button>
                        </>)}
                        {tab === "lights" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Ambient</div>
                            <Slider label="intensity" value={lights.ambientIntensity} min={0} max={2} onChange={set("ambientIntensity")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Key light</div>
                            <Slider label="intensity" value={lights.keyIntensity} min={0} max={3} onChange={set("keyIntensity")} />
                            <Slider label="x" value={lights.keyX} min={-20} max={20} step={0.5} onChange={set("keyX")} />
                            <Slider label="y" value={lights.keyY} min={-20} max={20} step={0.5} onChange={set("keyY")} />
                            <Slider label="z" value={lights.keyZ} min={-20} max={20} step={0.5} onChange={set("keyZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Fill light</div>
                            <Slider label="intensity" value={lights.fillIntensity} min={0} max={2} onChange={set("fillIntensity")} />
                            <Slider label="x" value={lights.fillX} min={-20} max={20} step={0.5} onChange={set("fillX")} />
                            <Slider label="y" value={lights.fillY} min={-20} max={20} step={0.5} onChange={set("fillY")} />
                            <Slider label="z" value={lights.fillZ} min={-20} max={20} step={0.5} onChange={set("fillZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Rim light</div>
                            <Slider label="intensity" value={lights.rimIntensity} min={0} max={2} onChange={set("rimIntensity")} />
                            <Slider label="x" value={lights.rimX} min={-20} max={20} step={0.5} onChange={set("rimX")} />
                            <Slider label="y" value={lights.rimY} min={-20} max={20} step={0.5} onChange={set("rimY")} />
                            <Slider label="z" value={lights.rimZ} min={-20} max={20} step={0.5} onChange={set("rimZ")} />
                        </>)}
                        {tab === "material" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Material (STL only)</div>
                            <Slider label="metalness" value={lights.metalness} min={0} max={1} onChange={set("metalness")} />
                            <Slider label="roughness" value={lights.roughness} min={0} max={1} onChange={set("roughness")} />
                        </>)}
                    </div>
                </>
            )}
        </div>
    );
}

function HierarchyTabBar({ activeProject, onSelect, navState, onNavState, hierarchy, showDesc, setShowDesc }) {
    const scrollRef = useRef(null);
    const isMobile = window.innerWidth < 640;

    // Helper to find current category object
    const currentCat = hierarchy.find(c => c.name === navState.category);

    const btnStyle = (isActive, isAccent = false) => ({
        border: "none", background: "none", cursor: "pointer", padding: "6px 12px",
        color: isActive ? "rgba(255,255,255,0.9)" : isAccent ? "rgba(255,200,80,0.5)" : "rgba(255,255,255,0.3)",
        fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase",
        fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
    });

    useEffect(() => {
        if (scrollRef.current) {
            const activeBtn = scrollRef.current.querySelector('[data-active="true"]');
            if (activeBtn) activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
    }, [activeProject.id, navState.level]);

    return (
        <div style={{ position: "absolute", bottom: "1.8rem", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", zIndex: 200, pointerEvents: "all", width: "100%" }}>
            {isMobile && showDesc && (
                <div style={{ maxWidth: "240px", textAlign: "center", marginBottom: "-3px", padding: "10px" }}>
                    <p style={{ margin: 0, fontSize: "13px", color: "rgba(255,255,255,0.8)", lineHeight: "1.6" }}>{activeProject.description}</p>
                </div>
            )}

            {/* 2. INSTRUCTION TEXT (Between Description and Name) */}
            <div style={{ marginBottom: "0.3rem", fontSize: "9px", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>
                drag to rotate · {isMobile ? "pinch to zoom" : "scroll to zoom"}
            </div>

            {/* NAME AND ICON - INLINE FLOW APPROACH */}
            <div style={{
                textAlign: "center",
                width: "100%",
                padding: "0 30px",
                marginBottom: "12px"
            }}>
                <div style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    position: "relative"
                }}>
                    <span style={{
                        fontFamily: "'Bitcount Grid Single', monospace",
                        fontSize: "2.0rem",
                        color: "rgba(255,255,255,0.7)",
                        lineHeight: 1.1,
                        verticalAlign: "middle"
                    }}>
                        {activeProject.name}

                        {isMobile && (
                            <button
                                onClick={() => setShowDesc(!showDesc)}
                                style={{
                                    display: "inline-flex", // Makes button behave like a text character
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "none",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    borderRadius: "50%",
                                    color: "rgba(255,255,255,0.4)",
                                    width: "22px",
                                    height: "22px",
                                    fontSize: "12px",
                                    marginLeft: "12px", // This is the EXACT gap between text and icon
                                    verticalAlign: "middle",
                                    cursor: "pointer",
                                    transform: "translateY(0px)" // Optically centers button with caps
                                }}
                            >
                                ?
                            </button>
                        )}
                    </span>
                </div>
            </div>

            {/* CATEGORY LEVEL */}
            {navState.level === "category" && (
                <div style={{ display: "flex", gap: "10px" }}>
                    {hierarchy.map(cat => (
                        <button key={cat.name} style={btnStyle(navState.category === cat.name)} onClick={() => {
                            if (cat.isFlat) {
                                // Skip Group Level, go straight to Model
                                onNavState({ level: "model", category: cat.name, group: null });
                            } else {
                                onNavState({ level: "group", category: cat.name });
                            }
                            onSelect(cat.groups[0].hero);
                        }}>{cat.name}</button>
                    ))}
                </div>
            )}

            {/* GROUP LEVEL */}
            {navState.level === "group" && (
                <div style={{ display: "flex", gap: "10px", alignItems: "center", maxWidth: "90vw" }}>
                    <button style={btnStyle(false, true)} onClick={() => onNavState({ level: "category" })}>BACK</button>
                    <div ref={scrollRef} style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
                        {currentCat?.groups.map(grp => (
                            <button key={grp.name} data-active={navState.group === grp.name} style={btnStyle(navState.group === grp.name)}
                                onClick={() => { onNavState({ level: "model", category: navState.category, group: grp.name }); onSelect(grp.hero); }}>
                                {grp.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* MODEL LEVEL */}
            {navState.level === "model" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center", maxWidth: "90vw" }}>
                    <button style={btnStyle(false, true)} onClick={() => {
                        // If category is flat, go back to Category level, otherwise go to Group level
                        if (currentCat?.isFlat) {
                            onNavState({ level: "category" });
                        } else {
                            onNavState({ level: "group", category: navState.category });
                        }
                    }}>BACK</button>
                    <div ref={scrollRef} style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
                        {currentCat?.groups.find(g => g.name === navState.group)?.models.map(p => (
                            <button key={p.id} data-active={activeProject.id === p.id} style={btnStyle(activeProject.id === p.id)} onClick={() => onSelect(p)}>{p.name}</button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── 3D Logic ──────────────────────────────────────────────────────────────────
function MeshController({ meshRef, autoRotate, rotationAxis, baseScaleRef, initialQuatRef, resetSignal, isMobile, scaleTargetRef }) {
    const { gl } = useThree();
    const drag = useRef({ active: false, lastX: 0, lastY: 0, velX: 0, velY: 0 });
    const pinch = useRef({ active: false, lastDist: 0 });
    const _ownScaleTarget = useRef(null);
    // Use the externally-shared ref when provided so the parent can sync scaleTarget
    const scaleTarget = scaleTargetRef ?? _ownScaleTarget;
    const scrollAccum = useRef(0);

    useEffect(() => {
        if (resetSignal === 0) return;
        if (!meshRef.current || !initialQuatRef.current) return;
        meshRef.current.quaternion.copy(initialQuatRef.current);
        if (baseScaleRef.current !== null) {
            scaleTarget.current = baseScaleRef.current;
            meshRef.current.scale.setScalar(baseScaleRef.current);
        }
        scrollAccum.current = 0;
        drag.current.velX = 0; drag.current.velY = 0;
    }, [resetSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const canvas = gl.domElement;
        const getXY = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
        const onDown = (e) => { if (e.touches && e.touches.length === 2) return; const { x, y } = getXY(e); drag.current = { active: true, lastX: x, lastY: y, velX: 0, velY: 0 }; };
        const onMove = (e) => {
            if (e.touches && e.touches.length === 2) { drag.current.active = false; return; }
            if (!drag.current.active || !meshRef.current) return;
            const { x, y } = getXY(e);
            const dx = x - drag.current.lastX; const dy = y - drag.current.lastY;
            drag.current.velX = dx; drag.current.velY = dy; drag.current.lastX = x; drag.current.lastY = y;
            const s = 0.008;
            meshRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx * s));
            meshRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy * s));
        };
        const onUp = () => { drag.current.active = false; };
        canvas.addEventListener("mousedown", onDown); canvas.addEventListener("mousemove", onMove); canvas.addEventListener("mouseup", onUp); canvas.addEventListener("mouseleave", onUp);
        canvas.addEventListener("touchstart", onDown, { passive: true }); canvas.addEventListener("touchmove", onMove, { passive: true }); canvas.addEventListener("touchend", onUp);
        return () => {
            canvas.removeEventListener("mousedown", onDown); canvas.removeEventListener("mousemove", onMove); canvas.removeEventListener("mouseup", onUp); canvas.removeEventListener("mouseleave", onUp);
            canvas.removeEventListener("touchstart", onDown); canvas.removeEventListener("touchmove", onMove); canvas.removeEventListener("touchend", onUp);
        };
    }, [gl, meshRef]);

    useEffect(() => {
        const canvas = gl.domElement;
        const onTouchStart = (e) => { if (e.touches.length === 2) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; pinch.current = { active: true, lastDist: Math.sqrt(dx * dx + dy * dy) }; } };
        const onTouchMove = (e) => {
            if (!pinch.current.active || e.touches.length !== 2 || !meshRef.current) return;
            const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (pinch.current.lastDist > 0 && baseScaleRef.current !== null) {
                const base = baseScaleRef.current; const ratio = dist / pinch.current.lastDist;
                const cur = scaleTarget.current ?? meshRef.current.scale.x;
                scaleTarget.current = Math.min(base * 10.0, Math.max(base * 0.1, cur * (1 + (ratio - 1) * 0.70)));
            }
            pinch.current.lastDist = dist;
        };
        const onTouchEnd = () => { pinch.current.active = false; };
        canvas.addEventListener("touchstart", onTouchStart, { passive: true }); canvas.addEventListener("touchmove", onTouchMove, { passive: true }); canvas.addEventListener("touchend", onTouchEnd);
        return () => { canvas.removeEventListener("touchstart", onTouchStart); canvas.removeEventListener("touchmove", onTouchMove); canvas.removeEventListener("touchend", onTouchEnd); };
    }, [gl, meshRef, baseScaleRef, isMobile, scaleTarget]);

    useEffect(() => {
        const canvas = gl.domElement;
        const onWheel = (e) => { e.preventDefault(); const raw = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 300 : e.deltaY; scrollAccum.current += raw * 0.15; };
        canvas.addEventListener("wheel", onWheel, { passive: false });
        return () => canvas.removeEventListener("wheel", onWheel);
    }, [gl]);

    useFrame((_, delta) => {
        if (!meshRef.current) return;
        if (scaleTarget.current === null && baseScaleRef.current !== null) scaleTarget.current = baseScaleRef.current;
        if (autoRotate && !drag.current.active && !pinch.current.active) {
            const axisMap = { x: new THREE.Vector3(1, 0, 0), y: new THREE.Vector3(0, 1, 0), z: new THREE.Vector3(0, 0, 1) };
            meshRef.current.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(axisMap[rotationAxis ?? "z"], delta * 0.2));
        }
        if (!drag.current.active && (Math.abs(drag.current.velX) > 0.05 || Math.abs(drag.current.velY) > 0.05)) {
            const s = 0.008;
            meshRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), drag.current.velX * s));
            meshRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), drag.current.velY * s));
            drag.current.velX *= 0.88; drag.current.velY *= 0.88;
        }
        if (Math.abs(scrollAccum.current) > 0.1 && baseScaleRef.current !== null && scaleTarget.current !== null) {
            const base = baseScaleRef.current; const nudge = scrollAccum.current * 0.0006;
            const minPull = isMobile ? base * 1.0 : base * 0.9; const maxPull = isMobile ? base * 10.0 : base * 2.2;
            scaleTarget.current = Math.min(maxPull, Math.max(minPull, scaleTarget.current * (1 - nudge)));
            scrollAccum.current *= 0.9; if (Math.abs(scrollAccum.current) < 0.1) scrollAccum.current = 0;
        }
        const isInteracting = pinch.current.active || Math.abs(scrollAccum.current) > 0.01;
        if (!isInteracting && baseScaleRef.current !== null && scaleTarget.current !== null) {
            const base = baseScaleRef.current;
            const minAllowed = isMobile ? base * 0.7 : base * 1.0; const maxAllowed = isMobile ? base * 2.5 : base * 2.1;
            if (scaleTarget.current < minAllowed) scaleTarget.current = minAllowed;
            else if (scaleTarget.current > maxAllowed) scaleTarget.current = maxAllowed;
        }
        if (scaleTarget.current !== null) {
            const cur = meshRef.current.scale.x; const diff = scaleTarget.current - cur;
            if (Math.abs(diff) > 0.00005) meshRef.current.scale.setScalar(cur + diff * 0.08);
        }
    });
    return null;
}

function STLModel({ url, rotation, autoRotate, rotationAxis, metalness, roughness, initialZoomPct, resetSignal, isMobile }) {
    const geometry = useLoader(STLLoader, url);
    const meshRef = useRef();
    const baseScaleRef = useRef(null);
    const initialQuatRef = useRef(null);
    const initializedUrl = useRef(null);
    // Shared scaleTarget passed into MeshController so slider changes can sync it
    const scaleTargetRef = useRef(null);

    // Always-current refs — avoid stale closures
    const initialZoomPctRef = useRef(initialZoomPct);
    const rotationRef = useRef(rotation);
    useEffect(() => { initialZoomPctRef.current = initialZoomPct; }, [initialZoomPct]);
    useEffect(() => { rotationRef.current = rotation; }, [rotation]);

    // Raw fit scale stored after first geometry load
    const fittedScaleRef = useRef(null);

    // Apply scale + rotation from current refs (no re-centering)
    const applyScaleAndRotation = useRef(null);
    applyScaleAndRotation.current = () => {
        if (!meshRef.current || fittedScaleRef.current === null) return;
        const finalScale = fittedScaleRef.current * ((initialZoomPctRef.current ?? 100) / 100);
        meshRef.current.scale.setScalar(finalScale);
        scaleTargetRef.current = finalScale; // keep MeshController in sync
        baseScaleRef.current = finalScale;
        const rot = rotationRef.current ?? [-Math.PI / 2, 0, 0];
        meshRef.current.quaternion.setFromEuler(new THREE.Euler(rot[0], rot[1], rot[2]));
        initialQuatRef.current = meshRef.current.quaternion.clone();
    };

    // First load / URL change: center geometry and compute fit scale
    useEffect(() => {
        if (!meshRef.current || !geometry || initializedUrl.current === url) return;

        geometry.center();
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const size = new THREE.Vector3();
        size.subVectors(box.max, box.min);

        fittedScaleRef.current = 4.5 / Math.max(size.x, size.y, size.z);
        applyScaleAndRotation.current();
        initializedUrl.current = url;
    }, [geometry, url]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-apply when debug sliders change zoom % or rotation
    useEffect(() => {
        if (!initializedUrl.current) return; // not yet loaded
        applyScaleAndRotation.current();
    }, [initialZoomPct, rotation]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <mesh ref={meshRef} geometry={geometry}>
                <meshStandardMaterial color="#d0d0d0" metalness={metalness} roughness={roughness} />
            </mesh>
            <MeshController
                meshRef={meshRef}
                autoRotate={autoRotate}
                rotationAxis={rotationAxis}
                baseScaleRef={baseScaleRef}
                initialQuatRef={initialQuatRef}
                resetSignal={resetSignal}
                isMobile={isMobile}
                scaleTargetRef={scaleTargetRef}
            />
        </>
    );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function Portfolio({ activePage, setActivePage }) { // Receives state from App.js
    const location = useLocation();
    const navigate = useNavigate();
    const hierarchy = useMemo(() => buildHierarchy(UNIQUE_PROJECTS), []);
    const initialProject = projectFromPath(location.pathname);
    const [active, setActive] = useState(initialProject ?? UNIQUE_PROJECTS[0]);
    const [navState, setNavState] = useState(() => initialProject ? navStateFor(initialProject, hierarchy) : { level: "category" });
    const [showDesc, setShowDesc] = useState(false);
    const [lights, setLights] = useState(DEFAULT_LIGHTS);
    const [paused, setPaused] = useState(false);
    const [modelDebug, setModelDebug] = useState(modelDebugDefaults(initialProject ?? UNIQUE_PROJECTS[0]));
    const [resetSignal, setResetSignal] = useState(0);
    const DEBUG_LIGHTING = false;

    const isMobile = window.innerWidth < 640;


    const rotationAxis = DEBUG_LIGHTING ? modelDebug.rotationAxis : (active.rotationAxis ?? "z");
    // ── Replace the old declarations with these ──
    const rotation = useMemo(() => {
        return DEBUG_LIGHTING
            ? [modelDebug.rotX, modelDebug.rotY, modelDebug.rotZ]
            : (active.rotation ?? [-Math.PI / 2, 0, 0]);
    }, [active.rotation, modelDebug.rotX, modelDebug.rotY, modelDebug.rotZ, DEBUG_LIGHTING]);

    const initialView = useMemo(() => {
        return DEBUG_LIGHTING
            ? [modelDebug.viewX, modelDebug.viewY, modelDebug.viewZ]
            : (active.initialView ?? [0, 0, 10]);
    }, [active.initialView, modelDebug.viewX, modelDebug.viewY, modelDebug.viewZ, DEBUG_LIGHTING]);

    const initialZoom = useMemo(() => {
        return DEBUG_LIGHTING
            ? modelDebug.initialZoom
            : (active.initialZoom ?? 100);
    }, [active.initialZoom, modelDebug.initialZoom, DEBUG_LIGHTING]);

    const handleSetActive = (p) => {
        setActive(p);
        setModelDebug(modelDebugDefaults(p));
        setShowDesc(false);
        setPaused(false);
        if (location.pathname !== projectPath(p)) navigate(projectPath(p), { replace: true });
    };

    // Keep the shown model in sync with the URL (back/forward, pasted links)
    useEffect(() => {
        if (location.pathname === "/") return;
        const p = projectFromPath(location.pathname);
        if (!p) { navigate("/", { replace: true }); return; }
        if (p.id !== active.id) {
            setActive(p);
            setModelDebug(modelDebugDefaults(p));
            setNavState(navStateFor(p, hierarchy));
            setShowDesc(false);
            setPaused(false);
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const isModelPage = location.pathname.startsWith("/work/");
    const pageUrl = isModelPage ? SITE_URL + projectPath(active) : SITE_URL + "/";
    const pageTitle = isModelPage ? `${active.name} | 3D CAD Model by Ibrahim Tayyab` : "Ibrahim Tayyab | Mechanical Engineer and 3D CAD Portfolio";
    const descSource = isModelPage
        ? `${active.description}${active.software ? ` Modeled in ${active.software}.` : ""}`
        : "Mechanical engineering portfolio of Ibrahim Tayyab: interactive 3D CAD models of industrial machinery, product design and manufacturing work.";
    const pageDesc = descSource.length > 160 ? descSource.slice(0, 157).trimEnd() + "..." : descSource;

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDesc} />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDesc} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:type" content={isModelPage ? "article" : "website"} />
                <meta property="og:site_name" content="Ibrahim Tayyab" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>
            <style>{`
                html, body, #root { height: 100%; width: 100%; margin: 0; padding: 0; overflow: hidden; }
                * { box-sizing: border-box; }
                ::-webkit-scrollbar { display: none; }
                @media (max-width: 640px) { .info-panel { display: none !important; } }
            `}</style>

            <div style={{ width: "100%", height: "100%", background: "#080808", overflow: "hidden", fontFamily: "'DM Sans', sans-serif", position: "fixed", top: 0, left: 0 }}>
                <div style={{ position: "absolute", top: "-85px", left: 0, right: 0, bottom: 0 }}>
                    <Canvas style={{ width: "100%", height: "100%", touchAction: "none" }} gl={{ antialias: true }}>
                        <color attach="background" args={["#080808"]} />
                        <PerspectiveCamera makeDefault position={initialView} fov={40} />
                        <ambientLight intensity={lights.ambientIntensity} />
                        <directionalLight position={[lights.keyX, lights.keyY, lights.keyZ]} intensity={lights.keyIntensity} />
                        <directionalLight position={[lights.fillX, lights.fillY, lights.fillZ]} intensity={lights.fillIntensity} color="#ddeeff" />
                        <directionalLight position={[lights.rimX, lights.rimY, lights.rimZ]} intensity={lights.rimIntensity} color="#aabbcc" />
                        <Suspense fallback={null}>
                            <STLModel key={active.id} url={assetUrl(active.url)} rotation={rotation} autoRotate={!paused}
                                rotationAxis={rotationAxis} metalness={lights.metalness} roughness={lights.roughness}
                                initialZoomPct={initialZoom} resetSignal={resetSignal} isMobile={isMobile} />
                        </Suspense>
                    </Canvas>
                </div>

                <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>

                    {activePage === "Work" && (
                        <>
                            <div className="info-panel" style={{ position: "absolute", top: "1.6rem", right: ".8rem", width: "220px", padding: "1rem", pointerEvents: "all" }}>
                                <p style={{ margin: 0, fontFamily: "'Bitcount Grid Single', monospace", fontSize: "1.75rem", color: "rgba(255,255,255,0.88)" }}>{active.name}</p>
                                <p style={{ margin: "10px 0 0", fontSize: "15px", color: "rgba(255,255,255,0.35)", lineHeight: 1.8 }}>{active.description}</p>
                            </div>
                            <HierarchyTabBar activeProject={active} onSelect={handleSetActive}
                                navState={navState} onNavState={setNavState}
                                hierarchy={hierarchy} showDesc={showDesc} setShowDesc={setShowDesc} />
                        </>
                    )}

                    {activePage === "About" && (
                        <div onClick={() => setActivePage("Work")} style={{ position: "absolute", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(8,8,8,0.82)", backdropFilter: "blur(22px)", pointerEvents: "all" }}>
                            <div onClick={e => e.stopPropagation()} style={{ maxWidth: "480px", width: "90%", background: "rgba(12,12,12,0.96)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "2.5rem" }}>
                                <p style={{ margin: "0 0 1.2rem", fontFamily: "'Bitcount Grid Single', monospace", fontSize: "2rem", color: "rgba(255,255,255,0.88)" }}>About</p>
                                <p style={{ margin: "0 0 1rem", fontSize: "16px", color: "rgba(255,255,255,0.5)", lineHeight: 1.9 }}>Mechanical Engineer focused on CAD, manufacturing, and product design.</p>
                                <button onClick={() => setActivePage("Work")} style={{ marginTop: "2rem", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "rgba(255,255,255,0.4)", fontSize: "11px", padding: "8px 20px", cursor: "pointer" }}>← Back</button>
                            </div>
                        </div>
                    )}

                    {activePage === "Contact" && (
                        <div onClick={() => setActivePage("Work")} style={{ position: "absolute", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(8,8,8,0.82)", backdropFilter: "blur(22px)", pointerEvents: "all" }}>
                            <div onClick={e => e.stopPropagation()} style={{ maxWidth: "400px", width: "90%", background: "rgba(12,12,12,0.96)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "2.5rem" }}>
                                <p style={{ margin: "0 0 1.6rem", fontFamily: "'Bitcount Grid Single', monospace", fontSize: "2rem", color: "rgba(255,255,255,0.88)" }}>Contact</p>
                                <a href="mailto:hi@ibrahimtayyab.com" style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: "16px" }}>hi@ibrahimtayyab.com</a>
                                <br />
                                <a href="https://www.linkedin.com/in/ibrahimtayyab" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: "16px", marginTop: "0.8rem", display: "inline-block" }}>linkedin.com/in/ibrahimtayyab</a>
                                <button onClick={() => setActivePage("Work")} style={{ marginTop: "2rem", display: "block", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "rgba(255,255,255,0.4)", fontSize: "11px", padding: "8px 20px", cursor: "pointer" }}>← Back</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {DEBUG_LIGHTING && (
                <LightDebugPanel
                    lights={lights}
                    onChange={setLights}
                    paused={paused}
                    onTogglePause={() => setPaused(p => !p)}
                    onReset={() => { setResetSignal(s => s + 1); setLights(DEFAULT_LIGHTS); setModelDebug(modelDebugDefaults(active)); setPaused(false); }}
                    modelDebug={modelDebug}
                    onModelDebugChange={setModelDebug}
                    activeProject={active}
                />
            )}
        </>
    );
}